/* DirectLED — Shared Submittal PDF / Schedule Excel generator.
   Callable from both the Quote drawer (logged-out) and the Order drawer
   (b2b-approved), so submittal/schedule packages can be built from whichever
   list the customer is actually using — no separate "add to submittal" step.

   Expects items in the shape: { title, sku, spec (url or ''), url, img, qty, type }
   Expects contact in the shape: { name, company, email, phone, job, dist, address, notes }
*/
(function () {
  function esc(s) {
    if (window.esc) return window.esc(s);
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Breaks text into lines that fit within maxWidth at the given font/size,
  // breaking on spaces first and falling back to a hard character break for
  // single words/tokens wider than maxWidth (e.g. long distributor names).
  function wrapPdfText(text, font, size, maxWidth) {
    var words = String(text || '').split(' ');
    var lines = []; var cur = '';
    words.forEach(function (w) {
      var test = cur ? cur + ' ' + w : w;
      if (font.widthOfTextAtSize(test, size) <= maxWidth) { cur = test; return; }
      if (cur) { lines.push(cur); cur = ''; }
      while (font.widthOfTextAtSize(w, size) > maxWidth && w.length > 1) {
        var lo = 1, hi = w.length, fit = 1;
        while (lo <= hi) {
          var mid = (lo + hi) >> 1;
          if (font.widthOfTextAtSize(w.substring(0, mid), size) <= maxWidth) { fit = mid; lo = mid + 1; } else { hi = mid - 1; }
        }
        lines.push(w.substring(0, fit));
        w = w.substring(fit);
      }
      cur = w;
    });
    if (cur) lines.push(cur);
    return lines.length ? lines : [''];
  }

  // Ian's own Google Apps Script Web App endpoint — logs each request as a
  // row in his Google Sheet and emails him directly from his own Google
  // account. No Shopify /contact endpoint involved, so no captcha, no bot
  // protection, no page reload — a plain silent background POST.
  var DL_NOTIFY_URL = 'https://script.google.com/macros/s/AKfycbzub0sczj7m3Pw9VYCNVJLe5Ongi1GDvGDm4X4fZqoQQ9ZXtEi5GsYROxAqe7ls4fDISQ/exec';

  function notifyRequest(action, items, contact) {
    if (!DL_NOTIFY_URL || DL_NOTIFY_URL.indexOf('script.google.com') === -1) {
      console.warn('DLDocs: notification endpoint not configured — set DL_NOTIFY_URL in dl-doc-generator.js');
      return;
    }
    var products = items.map(function (i) {
      return '- ' + i.title + ' | SKU: ' + (i.sku || 'N/A') + ' | Qty: ' + (i.qty || 1) + (i.type ? ' | Type: ' + i.type : '');
    }).join('\n');
    var fd = new URLSearchParams();
    fd.append('action', action);
    fd.append('name', contact.name || '');
    fd.append('company', contact.company || '');
    fd.append('email', contact.email || '');
    fd.append('phone', contact.phone || '');
    fd.append('job', contact.job || '');
    fd.append('dist', contact.dist || '');
    fd.append('notes', contact.notes || '');
    fd.append('products', products);
    fetch(DL_NOTIFY_URL, { method: 'POST', mode: 'no-cors', body: fd })
      .then(function () { console.log('DLDocs: notification sent (' + action + ')'); })
      .catch(function (err) { console.error('DLDocs: notification failed', err); });
  }

  var metaCache = {};
  // Resolves a product's metafields (collection, mounting, wattage, etc.) via
  // the ?view=metafields template, keyed by item.url. Only needed for the
  // Excel schedule's spec columns — spec sheet URLs are already carried on
  // the item itself, captured at add-time.
  function resolveItemMeta(item) {
    var cacheKey = item.sku || item.url;
    if (cacheKey && metaCache[cacheKey]) return Promise.resolve(metaCache[cacheKey]);

    function fetchByUrl(url) {
      var base = url.split('?')[0];
      return fetch(base + '?view=metafields').then(function (r) { return r.json(); });
    }

    var lookup;
    if (item.url) {
      lookup = fetchByUrl(item.url);
    } else if (item.sku) {
      var searchUrl = '/search/suggest.json?q=' + encodeURIComponent(item.sku) + '&resources[type]=product&resources[fields]=variants.sku&resources[limit]=1';
      lookup = fetch(searchUrl).then(function (r) { return r.json(); }).then(function (data) {
        var product = data && data.resources && data.resources.results && data.resources.results.products && data.resources.results.products[0];
        if (!product || !product.handle) throw new Error('no product found for SKU ' + item.sku);
        return fetchByUrl('/products/' + product.handle);
      });
    } else {
      lookup = Promise.resolve({ metafields: {} });
    }

    return lookup.then(function (json) {
      if (cacheKey) metaCache[cacheKey] = json;
      return json;
    }).catch(function (err) {
      console.warn('DLDocs: could not resolve product data for', item.sku || item.title, err);
      var fallback = { metafields: {} };
      if (cacheKey) metaCache[cacheKey] = fallback;
      return fallback;
    });
  }

  function loadPDFLib() {
    if (window.PDFLib) return Promise.resolve();
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js';
      s.onload = res; s.onerror = function () { rej(new Error('Could not load PDF library')); };
      document.head.appendChild(s);
    });
  }

  function loadExcelJS() {
    if (window.ExcelJS) return Promise.resolve();
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
      s.onload = res; s.onerror = function () { rej(new Error('Could not load spreadsheet library')); };
      document.head.appendChild(s);
    });
  }

  async function downloadSubmittalPDF(items, contact) {
    var job = contact.job || 'Unnamed Project';
    var dist = contact.dist || '';
    await loadPDFLib();
    var PDFLib = window.PDFLib;
    var merged = await PDFLib.PDFDocument.create();
    var font = await merged.embedFont(PDFLib.StandardFonts.Helvetica);
    var fontB = await merged.embedFont(PDFLib.StandardFonts.HelveticaBold);
    var green = PDFLib.rgb(0.259, 0.710, 0.286);
    var white = PDFLib.rgb(1, 1, 1);
    var dark = PDFLib.rgb(0.1, 0.1, 0.1);
    var grey = PDFLib.rgb(0.5, 0.5, 0.5);
    var lightgrey = PDFLib.rgb(0.88, 0.88, 0.88);

    /* Cover page */
    var cw = 612, ch = 792;
    var cover = merged.addPage([cw, ch]);
    cover.drawRectangle({ x: 0, y: ch - 90, width: cw, height: 90, color: green });
    cover.drawText('DirectLED', { x: 40, y: ch - 52, size: 30, font: fontB, color: white });
    cover.drawText('Lighting Fixture Submittal', { x: 40, y: ch - 74, size: 12, font: font, color: PDFLib.rgb(0.88, 1, 0.88) });
    var addr = ['31 West 34th Street, Suite 7125', 'New York, NY 10001', 'Tel: 646-535-4898', 'sales@directled.co'];
    addr.forEach(function (line, i) { var tw = font.widthOfTextAtSize(line, 8.5); cover.drawText(line, { x: cw - tw - 32, y: ch - 26 - (i * 12), size: 8.5, font: font, color: PDFLib.rgb(0.92, 1, 0.92) }); });
    cover.drawLine({ start: { x: 40, y: ch - 115 }, end: { x: cw - 40, y: ch - 115 }, thickness: 0.8, color: lightgrey });
    var maxTextWidth = cw - 80;
    var cursorY = ch - 140;
    cover.drawText('JOB NAME', { x: 40, y: cursorY, size: 9, font: fontB, color: grey });
    cursorY -= 20;
    wrapPdfText(job, fontB, 20, maxTextWidth).forEach(function (line) {
      cover.drawText(line, { x: 40, y: cursorY, size: 20, font: fontB, color: dark });
      cursorY -= 24;
    });
    cursorY -= 14;
    if (dist) {
      cover.drawText('DISTRIBUTOR', { x: 40, y: cursorY, size: 9, font: fontB, color: grey });
      cursorY -= 16;
      wrapPdfText(dist, font, 13, maxTextWidth).forEach(function (line) {
        cover.drawText(line, { x: 40, y: cursorY, size: 13, font: font, color: dark });
        cursorY -= 16;
      });
      cursorY -= 14;
    }
    var detailY = cursorY;
    cover.drawText('Date: ' + new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), { x: 40, y: detailY, size: 10, font: font, color: grey });
    var tableY = detailY - 50;
    cover.drawRectangle({ x: 40, y: tableY, width: cw - 80, height: 16, color: PDFLib.rgb(0.95, 0.95, 0.95) });
    cover.drawText('PRODUCT', { x: 48, y: tableY + 4, size: 8, font: fontB, color: grey });
    cover.drawText('TYPE', { x: 380, y: tableY + 4, size: 8, font: fontB, color: grey });
    items.forEach(function (item, idx) {
      var y = tableY - 20 - (idx * 22); if (y < 60) return;
      cover.drawLine({ start: { x: 40, y: y + 14 }, end: { x: cw - 40, y: y + 14 }, thickness: 0.3, color: PDFLib.rgb(0.93, 0.93, 0.93) });
      cover.drawText(String(item.title || '').substring(0, 50), { x: 48, y: y + 2, size: 9, font: fontB, color: dark });
      cover.drawText(String(item.type || '\u2014').substring(0, 28), { x: 380, y: y + 2, size: 9, font: font, color: grey });
    });
    cover.drawLine({ start: { x: 40, y: 36 }, end: { x: cw - 40, y: 36 }, thickness: 0.5, color: lightgrey });
    cover.drawText('DirectLED \u2014 directled.co \u2014 sales@directled.co \u2014 646-535-4898', { x: 40, y: 22, size: 7.5, font: font, color: grey });

    /* Merge spec sheets */
    var loaded = 0;
    for (var i = 0; i < items.length; i++) {
      if (!items[i].spec) continue;
      try {
        var specUrl = String(items[i].spec).replace(/^\/\//, 'https://');
        var resp = await fetch(specUrl, { mode: 'cors' });
        var bytes = await resp.arrayBuffer();
        var doc = await PDFLib.PDFDocument.load(bytes, { ignoreEncryption: true });
        var modelLabel = String(items[i].sku || items[i].title || '').substring(0, 40);
        var typeLabel = String(items[i].type || '').substring(0, 40);
        var headerText = 'MODEL: ' + modelLabel + (typeLabel ? '   |   TYPE: ' + typeLabel : '') + (dist ? '   |   DISTRIBUTOR: ' + dist : '');
        var srcPages = doc.getPages();
        for (var pi = 0; pi < srcPages.length; pi++) {
          var embedded = await merged.embedPage(srcPages[pi]);
          var origW = embedded.width, origH = embedded.height;
          var hdrSize = 9, lineHeight = hdrSize + 4, padX = 10, padY = 6;
          var maxWidth = origW - (padX * 2);
          var lines = wrapPdfText(headerText, fontB, hdrSize, maxWidth);
          var barHeight = (lines.length * lineHeight) + (padY * 2) - 2;
          var newPage = merged.addPage([origW, origH]);
          var scale = (origH - barHeight) / origH;
          var drawW = origW * scale, drawH = origH * scale;
          newPage.drawPage(embedded, { x: (origW - drawW) / 2, y: 0, xScale: scale, yScale: scale });
          newPage.drawRectangle({ x: 0, y: origH - barHeight, width: origW, height: barHeight, color: white });
          lines.forEach(function (line, li) {
            newPage.drawText(line, { x: padX, y: origH - padY - hdrSize - (li * lineHeight), size: hdrSize, font: fontB, color: dark });
          });
          newPage.drawLine({ start: { x: 0, y: origH - barHeight }, end: { x: origW, y: origH - barHeight }, thickness: 1.2, color: dark });
        }
        loaded++;
      } catch (e) { console.warn('Could not load spec for', items[i].title, e); }
    }
    if (!loaded) throw new Error('Could not load any spec sheets for the items in this list.');

    /* Download */
    var pdfBytes = await merged.save();
    var blob = new Blob([pdfBytes], { type: 'application/pdf' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = 'DirectLED-Submittal-' + job.replace(/\s+/g, '-') + '.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  var SCHEDULE_COLUMNS = [
    { header: 'TYPE', width: 10, get: function (item) { return item.type || ''; } },
    { header: 'DESCRIPTION', width: 34, get: function (item, meta) { return meta.collection || meta.title || item.title || ''; } },
    { header: 'MOUNTING', width: 16, get: function (item, meta) { return (meta.metafields && meta.metafields.mounting) || ''; } },
    { header: 'DIMENSION', width: 14, get: function (item, meta) { return (meta.metafields && meta.metafields.dimensions) || ''; } },
    { header: 'MANUFACTURER', width: 15, get: function () { return 'DirectLED'; } },
    { header: 'MODEL NUMBER', width: 24, get: function (item) { return (item.sku || '').toUpperCase(); } },
    { header: 'COUNT', width: 7, get: function (item) { return item.qty || 1; } },
    { header: 'SOURCE TYPE', width: 12, get: function () { return 'Integral LED'; } },
    { header: 'LAMP QTY', width: 9, get: function () { return 1; } },
    { header: 'LUMENS', width: 12, get: function (item, meta) { var mf = (meta.metafields) || {}; if (mf.lumens) return mf.lumens; if (mf.lumen_per_foot) return mf.lumen_per_foot + ' lm/ft'; return ''; } },
    { header: 'WATTAGE', width: 11, get: function (item, meta) { var mf = (meta.metafields) || {}; if (mf.wattage) return mf.wattage; if (mf.wattage_per_foot) return mf.wattage_per_foot + ' W/ft'; return ''; } },
    { header: 'CCT', width: 16, get: function (item, meta) { return (meta.metafields && meta.metafields.cct) || ''; } },
    { header: 'CRI', width: 8, get: function (item, meta) { return (meta.metafields && meta.metafields.cri) || ''; } },
    { header: 'VOLTS', width: 9, get: function (item, meta) { return (meta.metafields && meta.metafields.voltage) || ''; } },
    { header: 'DIM TYPE', width: 10, get: function (item, meta) { return (meta.metafields && meta.metafields.dimming) || ''; } },
    { header: 'REMARKS', width: 30, get: function () { return ''; } }
  ];

  async function generateScheduleExcel(items, contact) {
    await loadExcelJS();
    var metas = await Promise.all(items.map(resolveItemMeta));

    var wb = new ExcelJS.Workbook();
    var ws = wb.addWorksheet('Fixture Schedule');
    ws.columns = SCHEDULE_COLUMNS.map(function (col) { return { width: col.width }; });

    [ 'DESIGNER: ' + contact.name + (contact.company ? ' (' + contact.company + ')' : ''),
      'JOB NAME: ' + (contact.job || ''),
      'ADDRESS: ' + (contact.address || '')
    ].forEach(function (text) {
      var row = ws.addRow([text]);
      row.getCell(1).font = { name: 'Arial', size: 11, bold: true };
    });
    ws.addRow([]);

    var headerRow = ws.addRow(SCHEDULE_COLUMNS.map(function (col) { return col.header; }));
    headerRow.eachCell(function (cell) {
      cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
    headerRow.height = 26;

    items.forEach(function (item, i) {
      var meta = metas[i] || {};
      var rowValues = SCHEDULE_COLUMNS.map(function (col) { return col.get(item, meta); });
      var row = ws.addRow(rowValues);
      row.eachCell(function (cell) {
        cell.font = { name: 'Arial', size: 10, color: { argb: 'FF000000' } };
        cell.alignment = { vertical: 'top', wrapText: true };
        cell.border = { top: { style: 'thin', color: { argb: 'FF000000' } }, left: { style: 'thin', color: { argb: 'FF000000' } }, bottom: { style: 'thin', color: { argb: 'FF000000' } }, right: { style: 'thin', color: { argb: 'FF000000' } } };
      });
      if (i % 2 === 1) {
        row.eachCell(function (cell) { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } }; });
      }
    });

    ws.views = [{ state: 'frozen', ySplit: headerRow.number }];
    ws.autoFilter = { from: 'A' + headerRow.number, to: String.fromCharCode(64 + SCHEDULE_COLUMNS.length) + headerRow.number };

    var buffer = await wb.xlsx.writeBuffer();
    var blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    var fname = (contact.job ? contact.job.replace(/[^a-z0-9]+/gi, '_') : 'DirectLED_Schedule') + '.xlsx';
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = fname; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  }

  window.DLDocs = {
    notifyRequest: notifyRequest,
    downloadSubmittalPDF: downloadSubmittalPDF,
    generateScheduleExcel: generateScheduleExcel
  };
})();
